import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import styles from './CreateDocModal.module.scss'
import SignerSelector from './SignerSelector'
import DocEditorSection from './DocEditorSection'
import SearchPicker from './SearchPicker'
import { getDocflowEmployees, createDocument, updateDocument } from '../../../../service/DocflowService'

const DOC_TYPES = [
  { value: 'application',   label: "Заявление" },
  { value: 'service_note',  label: "Служебная записка" },
  { value: 'appeal',        label: "Обращение" },
  { value: 'notification',  label: "Уведомление" },
  { value: 'order',         label: "Приказ" },
  { value: 'instruction',   label: "Инструкция" },
  { value: 'report',        label: "Отчёт" },
  { value: 'contract',      label: "Договор" },
  { value: 'reference',     label: "Справка" },
  { value: 'memo',          label: "Докладная записка" },
  { value: 'explanatory',   label: "Объяснительная" },
  { value: 'protocol',      label: "Протокол" },
  { value: 'business_plan', label: "Бизнес-план" },
  { value: 'other',         label: "Иное" },
]

export default function CreateDocModal({ onClose, onCreated, editDoc, onUpdated }) {
  useInterfaceLanguage();
  const isEdit = !!editDoc
  const [employees, setEmployees]       = useState([])
  const [step, setStep]                 = useState(1)
  const [docType, setDocType]           = useState(
    editDoc ? (DOC_TYPES.find(t => t.value === editDoc.doc_type) ?? null) : null
  )
  const [subject, setSubject]           = useState(editDoc?.subject ?? '')
  const [resolutionEmp, setResolutionEmp] = useState(
    editDoc?.resolution_employee_id
      ? { id: editDoc.resolution_employee_id, full_name: editDoc.resolution_name }
      : null
  )
  const [signers, setSigners]           = useState(
    editDoc?.signers?.map(s => ({
      id: s.employee_id,
      full_name: s.employee_name,
      position: s.employee_position,
      department: s.employee_dept,
    })) ?? []
  )
  const [editorMode, setEditorMode]     = useState(editDoc?.main_file_name ? 'file' : 'text')
  const [content, setContent]           = useState(editDoc?.content ?? '')
  const [mainFile, setMainFile]         = useState(null)
  const [attachments, setAttachments]   = useState([])
  const [loading, setLoading]           = useState(false)
  const [errors, setErrors]             = useState({})

  useEffect(() => {
    getDocflowEmployees().then(r => setEmployees(r.data)).catch(() => {})
  }, [])

  function validate() {
    const e = {}
    if (!docType)           e.docType      = tr("Выберите тип")
    if (!subject.trim())    e.subject      = tr("Введите тему")
    if (!resolutionEmp)     e.resolution   = tr("Выберите ответственного")
    if (editorMode === 'text' && !content.trim()) e.content  = tr("Введите содержание")
    if (editorMode === 'file' && !mainFile && !editDoc?.main_file_name) e.mainFile = tr("Прикрепите PDF")
    setErrors(e)
    return !Object.keys(e).length
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!validate()) return

    const fd = new FormData()
    fd.append('doc_type',            docType.value)
    fd.append('subject',             subject)
    fd.append('to_employee',         resolutionEmp.id)
    fd.append('resolution_employee', resolutionEmp.id)
    if (editorMode === 'text') {
      fd.append('content', content)
    } else if (mainFile) {
      fd.append('main_file', mainFile)
    }
    if (isEdit) fd.append('update_signers', 'true')
    signers.forEach((s, i) => fd.append(`signer_${i + 1}`, s.id))
    if (!isEdit) attachments.forEach(f => fd.append('attachments', f))

    setLoading(true)
    try {
      let res
      if (isEdit) {
        res = await updateDocument(editDoc.id, fd)
        onUpdated?.(res.data)
      } else {
        res = await createDocument(fd)
        onCreated(res.data)
      }
    } catch (err) {
      const d = err.response?.data
      if (d) setErrors(d)
    } finally {
      setLoading(false)
    }
  }

  function addAttachments(files) {
    setAttachments(prev => [...prev, ...Array.from(files)])
  }

  function removeAttachment(idx) {
    setAttachments(prev => prev.filter((_, i) => i !== idx))
  }

  function fmtSize(bytes) {
    if (bytes < 1024) return tr("{{value0}} Б", { value0: bytes })
    if (bytes < 1024 * 1024) return tr("{{value0}} КБ", { value0: (bytes / 1024).toFixed(0) })
    return tr("{{value0}} МБ", { value0: (bytes / 1024 / 1024).toFixed(1) })
  }

  // items for SearchPicker
  const docTypeItems = DOC_TYPES.map(t => ({ value: t.value, label: t.label, avatar: null }))
  const employeeItems = employees.map(e => ({
    value:  e.id,
    label:  e.full_name,
    sub1:   e.position  || null,
    sub2:   e.department || null,
    avatar: e.full_name?.[0]?.toUpperCase() || '?',
  }))

  const modal = (
    <div className={styles.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        {/* ─ Header ─ */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerOrg}>{tr("🏛️ ОАО «КЫРГЫЗКОМУР»")}</div>
            <h2 className={styles.title}>{isEdit ? tr("Доработать документ") : tr("Создать документ")}</h2>
            <p className={styles.subtitle}>{isEdit ? tr("Редактирование: {{value0}}", { value0: editDoc.reg_number }) : tr("Новый исходящий документ — заполните все 4 шага")}</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} title={tr("Закрыть")}>×</button>
        </div>

        {/* ─ Steps indicator ─ */}
        <div className={styles.steps}>
          {[
            { label: "Реквизиты", desc: tr("Тип, тема, получатель") },
            { label: "Подписанты", desc: tr("Цепочка согласования") },
            { label: "Содержание", desc: tr("Текст или PDF-файл") },
            { label: "Вложения", desc: tr("Доп. файлы") },
          ].map((s, i) => (
            <div key={i} className={`${styles.step} ${step === i + 1 ? styles.stepActive : ''} ${step > i + 1 ? styles.stepDone : ''}`}>
              <span className={styles.stepNum}>{step > i + 1 ? '✓' : i + 1}</span>
              <div className={styles.stepText}>
                <span className={styles.stepLabel}>{tr(s.label)}</span>
                <span className={styles.stepDesc}>{tr(s.desc)}</span>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.body}>

            {/* ── Step 1: Реквизиты ── */}
            {tr(step === 1 && (
              <div className={styles.section}>
                <SearchPicker
                  label={tr("Тип документа")}
                  required
                  items={docTypeItems}
                  value={docType?.value ?? null}
                  onChange={item => setDocType(item)}
                  placeholder={tr("Поиск типа…")}
                  emptyLabel={tr("— Выберите тип —")}
                  error={errors.docType}
                />
                <div className={styles.field}>
                  <label>{tr("Тема документа ")}<span className={styles.req}>*</span></label>
                  <input
                    className={errors.subject ? styles.fieldErr : ''}
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder={tr("Например: О предоставлении отпуска с сохранением содержания")}
                    maxLength={500}
                  />
                  {tr(errors.subject && <span className={styles.err}>{tr(errors.subject)}</span>)}
                </div>
                <SearchPicker
                  label={tr("Ответственный (резолюция)")}
                  required
                  items={employeeItems}
                  value={resolutionEmp?.id ?? null}
                  onChange={item => setResolutionEmp(item ? { id: item.value, full_name: item.label, position: item.sub1, department: item.sub2 } : null)}
                  placeholder={tr("Поиск по ФИО, должности, отделу…")}
                  emptyLabel={tr("— Выберите сотрудника —")}
                  error={errors.resolution}
                />
              </div>
            ))}

            {/* ── Step 2: Подписанты ── */}
            {tr(step === 2 && (
              <div className={styles.section}>
                <div className={styles.sectionHdr}>
                  <h3>{tr("👤 Цепочка подписания")}</h3>
                  <p>{tr("Kто должен подписать документ до отправки получателю. Добавьте подписантов в порядке подписания.")}</p>
                </div>
                <SignerSelector signers={signers} onChange={setSigners} />
                {tr(signers.length === 0 && (
                  <p className={styles.hint}>{tr("ℹ️ Подписанты не речете? Тогда документ сразу перейдёт в работу ответственному.")}</p>
                ))}
              </div>
            ))}

            {/* ── Step 3: Содержание ── */}
            {tr(step === 3 && (
              <div className={styles.section}>
                <DocEditorSection
                  mode={editorMode} setMode={setEditorMode}
                  content={content} setContent={setContent}
                  mainFile={mainFile} setMainFile={setMainFile}
                  existingFileName={isEdit ? editDoc.main_file_name : null}
                />
                {tr(errors.content && <span className={styles.err}>{tr(errors.content)}</span>)}
                {tr(errors.mainFile && <span className={styles.err}>{tr(errors.mainFile)}</span>)}
              </div>
            ))}

            {/* ── Step 4: Вложения ── */}
            {tr(step === 4 && (
              <div className={styles.section}>
                <div className={styles.sectionHdr}>
                  <h3>{tr("🗎 Дополнительные вложения")}</h3>
                  <p>{tr("Необязательно. Прикрепите любые сопроводительные файлы (справки, таблицы, фото и др.).")}</p>
                </div>
                <label className={styles.attachDropLabel}>
                  <span>📎</span>
                  <strong>{tr("Выбрать файлы")}</strong>
                  <input type="file" multiple hidden onChange={e => addAttachments(e.target.files)} />
                </label>
                {tr(attachments.length > 0 && (
                  <ul className={styles.attachList}>
                    {attachments.map((f, i) => (
                      <li key={i} className={styles.attachItem}>
                        <span className={styles.attachIcon}>📄</span>
                        <span className={styles.attachName}>{tr(f.name)}</span>
                        <span className={styles.attachSize}>{fmtSize(f.size)}</span>
                        <button type="button" className={styles.removeFileBtn} onClick={() => removeAttachment(i)}>×</button>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            ))}
          </div>

          {/* ─ Footer ─ */}
          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>{tr("✕ Отмена")}</button>
            <div className={styles.stepInfo}>{tr("Шаг ")}{tr(step)}{tr(" из 4")}</div>
            <div className={styles.navBtns}>
              {tr(step > 1 && (
                <button type="button" className={styles.prevBtn} onClick={() => setStep(s => s - 1)}>{tr("← Назад")}</button>
              ))}
              {step < 4 ? (
                <button key="next" type="button" className={styles.nextBtn} onClick={() => setStep(s => s + 1)}>{tr("Далее →")}</button>
              ) : (
                <button key="submit" type="submit" className={styles.submitBtn} disabled={loading}>
                  {loading ? tr("Отправка…") : isEdit ? tr("✓ Повторно отправить") : tr("✓ Отправить документ")}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  )

  return ReactDOM.createPortal(modal, document.body)
}
